import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Save, Close, Tune } from '@mui/icons-material';
import {
  firebaseAdminService,
  AdminModifierGroup,
  AdminModifierOption,
} from '../../services/firebaseAdminService';

type OptionDraft = Partial<AdminModifierOption> & { _key?: string };

const ModifiersPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<AdminModifierGroup[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AdminModifierGroup | null>(null);
  const [draft, setDraft] = useState<Partial<AdminModifierGroup>>({
    name: '',
    name_de: '',
    type: 'single',
    required: false,
    applies_to_size: false,
    display_order: 0,
    is_active: true,
  });
  const [optionDrafts, setOptionDrafts] = useState<OptionDraft[]>([]);
  const [sizeGroupForMatrix, setSizeGroupForMatrix] = useState<AdminModifierGroup | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const g = await firebaseAdminService.getModifierGroups();
      // Hydrate options for each so the table can show count
      const withOptions = await Promise.all(
        g.map((grp) => firebaseAdminService.getModifierGroupWithOptions(grp.id).then((full) => full || grp))
      );
      setGroups(withOptions);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to load modifier groups', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNewGroup = () => {
    setEditingGroup(null);
    setDraft({
      name: '',
      name_de: '',
      type: 'single',
      required: false,
      applies_to_size: false,
      display_order: groups.length,
      is_active: true,
    });
    setOptionDrafts([]);
    setSizeGroupForMatrix(null);
    setEditorOpen(true);
  };

  const openEditGroup = async (group: AdminModifierGroup) => {
    const full = await firebaseAdminService.getModifierGroupWithOptions(group.id);
    if (!full) return;
    setEditingGroup(full);
    setDraft({ ...full });
    setOptionDrafts((full.options || []).map((o) => ({ ...o, _key: o.id })));
    // If this group is NOT applies_to_size, find the first applies_to_size group
    if (!full.applies_to_size) {
      const sizeGroup = groups.find((g) => g.applies_to_size && g.options && g.options.length > 0);
      setSizeGroupForMatrix(sizeGroup || null);
    } else {
      setSizeGroupForMatrix(null);
    }
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingGroup(null);
    setOptionDrafts([]);
  };

  const addOptionDraft = () => {
    setOptionDrafts((prev) => [
      ...prev,
      {
        _key: `new-${Date.now()}-${Math.random()}`,
        name: '',
        name_de: '',
        price_delta: 0,
        is_default: false,
        is_available: true,
        display_order: prev.length,
      },
    ]);
  };

  const updateOptionDraft = (key: string, patch: Partial<OptionDraft>) => {
    setOptionDrafts((prev) => prev.map((o) => (o._key === key ? { ...o, ...patch } : o)));
  };

  const removeOptionDraft = (key: string) => {
    setOptionDrafts((prev) => prev.filter((o) => o._key !== key));
  };

  const saveGroup = async () => {
    if (!draft.name || draft.name.trim() === '') {
      setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
      return;
    }
    try {
      let groupId: string;
      if (editingGroup) {
        await firebaseAdminService.updateModifierGroup(editingGroup.id, draft);
        groupId = editingGroup.id;
      } else {
        const created = await firebaseAdminService.createModifierGroup(draft);
        groupId = created.id;
      }

      // Sync options: update existing, create new, delete removed
      const existingIds = new Set((editingGroup?.options || []).map((o) => o.id));
      const keptIds = new Set<string>();

      for (const opt of optionDrafts) {
        if (opt.id && existingIds.has(opt.id)) {
          keptIds.add(opt.id);
          await firebaseAdminService.updateModifierOption(opt.id, opt);
        } else {
          await firebaseAdminService.createModifierOption({ ...opt, group_id: groupId });
        }
      }
      // Delete removed
      for (const existingOpt of editingGroup?.options || []) {
        if (!keptIds.has(existingOpt.id)) {
          await firebaseAdminService.deleteModifierOption(existingOpt.id);
        }
      }

      setSnackbar({ open: true, message: 'Saved', severity: 'success' });
      closeEditor();
      await load();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    }
  };

  const deleteGroup = async (group: AdminModifierGroup) => {
    if (!window.confirm(`Delete group "${group.name}"? All its options will also be deleted.`)) return;
    try {
      await firebaseAdminService.deleteModifierGroup(group.id);
      setSnackbar({ open: true, message: 'Deleted', severity: 'success' });
      await load();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--admin-text-primary)' }}>
              <Tune sx={{ verticalAlign: 'middle', mr: 1 }} />
              Modifier Groups
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--admin-text-secondary)' }}>
              Configure Size / Crust / Sauce / Topping popups per item (Domino's-style). Attach groups to items on the Menu page.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={openNewGroup} sx={{ bgcolor: '#00C896', color: '#000', '&:hover': { bgcolor: '#7bc211' } }}>
            New Group
          </Button>
        </Box>

        <Paper sx={{ bgcolor: 'var(--admin-bg-card)', border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.03)' }}>
                <TableCell sx={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}>Required</TableCell>
                <TableCell sx={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}>Size-Variant</TableCell>
                <TableCell sx={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}>Options</TableCell>
                <TableCell sx={{ color: 'var(--admin-text-primary)', fontWeight: 700, width: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'var(--admin-text-secondary)' }}>
                    Loading…
                  </TableCell>
                </TableRow>
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'var(--admin-text-secondary)' }}>
                    No modifier groups yet. Click "New Group" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((g) => (
                  <TableRow key={g.id} hover>
                    <TableCell sx={{ color: 'var(--admin-text-primary)' }}>
                      <Typography sx={{ fontWeight: 600 }}>{g.name}</Typography>
                      {g.name_de && (
                        <Typography variant="caption" sx={{ color: 'var(--admin-text-secondary)' }}>
                          DE: {g.name_de}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={g.type === 'single' ? 'Single-choice' : 'Multi-choice'}
                        size="small"
                        sx={{ bgcolor: g.type === 'single' ? '#2d4a7a' : '#4a2d7a', color: '#fff' }}
                      />
                    </TableCell>
                    <TableCell>{g.required ? <Chip label="Required" size="small" color="error" /> : <Chip label="Optional" size="small" />}</TableCell>
                    <TableCell>{g.applies_to_size ? <Chip label="Size" size="small" color="warning" /> : '—'}</TableCell>
                    <TableCell sx={{ color: 'var(--admin-text-secondary)' }}>{g.options?.length || 0}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditGroup(g)} sx={{ color: '#00C896' }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteGroup(g)} sx={{ color: '#ff5252' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* Editor dialog */}
        <Dialog
          open={editorOpen}
          onClose={closeEditor}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#0A2540',
              backgroundImage: 'none',
              color: '#e0e0e0',
              border: '1px solid #2a2d3e',
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            },
          }}
          BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' } }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid #2a2d3e', color: '#ffffff', fontWeight: 700 }}>
            {editingGroup ? 'Edit Modifier Group' : 'New Modifier Group'}
          </DialogTitle>
          <DialogContent sx={{ pt: '24px !important', pb: 2, overflowY: 'auto' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2, mt: 1 }}>
              <TextField label="Name (EN)" value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} fullWidth size="small" />
              <TextField label="Name (DE)" value={draft.name_de || ''} onChange={(e) => setDraft({ ...draft, name_de: e.target.value })} fullWidth size="small" />
            </Box>

            {/* Row 2: Type + Display Order */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={draft.type || 'single'} label="Type" onChange={(e) => setDraft({ ...draft, type: e.target.value as any })}>
                  <MuiMenuItem value="single">Single-choice (radio)</MuiMenuItem>
                  <MuiMenuItem value="multi">Multi-choice (checkbox)</MuiMenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Display Order"
                type="number"
                value={draft.display_order ?? 0}
                onChange={(e) => setDraft({ ...draft, display_order: parseInt(e.target.value) || 0 })}
                size="small"
                fullWidth
              />
            </Box>

            {/* Row 3: Toggle switches on their own row so long labels don't wrap */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
                mb: 2,
                p: 1.5,
                bgcolor: 'rgba(255,255,255,0.02)',
                border: '1px solid #2a2d3e',
                borderRadius: 1,
              }}
            >
              <FormControlLabel
                sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
                labelPlacement="start"
                control={<Switch checked={!!draft.required} onChange={(e) => setDraft({ ...draft, required: e.target.checked })} />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#e0e0e0' }}>Required</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>Pflichtfeld · must pick before adding</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
                labelPlacement="start"
                control={<Switch checked={!!draft.applies_to_size} onChange={(e) => setDraft({ ...draft, applies_to_size: e.target.checked })} />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#e0e0e0' }}>Size-variant group</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>Drives base price (Medium / Large)</Typography>
                  </Box>
                }
              />
            </Box>

            {draft.type === 'multi' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <TextField
                  label="Min select"
                  type="number"
                  value={draft.min_select ?? ''}
                  onChange={(e) => setDraft({ ...draft, min_select: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                  size="small"
                />
                <TextField
                  label="Max select"
                  type="number"
                  value={draft.max_select ?? ''}
                  onChange={(e) => setDraft({ ...draft, max_select: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                  size="small"
                />
              </Box>
            )}

            <Divider sx={{ my: 2, borderColor: 'var(--admin-border)' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                Options
              </Typography>
              <Button size="small" startIcon={<Add />} onClick={addOptionDraft} sx={{ color: '#00C896' }}>
                Add Option
              </Button>
            </Box>

            {sizeGroupForMatrix && (
              <Alert severity="info" sx={{ mb: 1, fontSize: '0.8rem' }}>
                Size-dependent pricing available. For each option, set per-size deltas below (e.g. Cheddar: Medium +€1.60, Large +€2.00).
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {optionDrafts.map((opt) => (
                <Paper key={opt._key} variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.02)', borderColor: '#2a2d3e' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 110px 40px' }, gap: 1, alignItems: 'center' }}>
                    <TextField label="Name (EN)" value={opt.name || ''} onChange={(e) => updateOptionDraft(opt._key!, { name: e.target.value })} size="small" />
                    <TextField label="Name (DE)" value={opt.name_de || ''} onChange={(e) => updateOptionDraft(opt._key!, { name_de: e.target.value })} size="small" />
                    <TextField
                      label="Price (€)"
                      type="number"
                      inputProps={{ step: 0.01 }}
                      value={opt.price_delta ?? 0}
                      onChange={(e) => updateOptionDraft(opt._key!, { price_delta: parseFloat(e.target.value) || 0 })}
                      size="small"
                    />
                    <IconButton size="small" onClick={() => removeOptionDraft(opt._key!)} sx={{ color: '#ff5252', justifySelf: 'center' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={<Switch size="small" checked={!!opt.is_default} onChange={(e) => updateOptionDraft(opt._key!, { is_default: e.target.checked })} />}
                      label={<Typography variant="caption" sx={{ color: '#9ca3af' }}>Default (pre-selected)</Typography>}
                    />
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={<Switch size="small" checked={opt.is_available !== false} onChange={(e) => updateOptionDraft(opt._key!, { is_available: e.target.checked })} />}
                      label={<Typography variant="caption" sx={{ color: '#9ca3af' }}>Available</Typography>}
                    />
                    <TextField
                      label="Display order"
                      type="number"
                      value={opt.display_order ?? 0}
                      onChange={(e) => updateOptionDraft(opt._key!, { display_order: parseInt(e.target.value) || 0 })}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {sizeGroupForMatrix && sizeGroupForMatrix.options && sizeGroupForMatrix.options.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed var(--admin-border)' }}>
                      <Typography variant="caption" sx={{ color: 'var(--admin-text-secondary)' }}>
                        Per-size price deltas (leave blank to use base delta above):
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        {sizeGroupForMatrix.options.map((sizeOpt) => (
                          <TextField
                            key={sizeOpt.id}
                            label={`${sizeOpt.name} (€)`}
                            type="number"
                            inputProps={{ step: 0.01 }}
                            value={opt.price_delta_by_size?.[sizeOpt.id] ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              const next = { ...(opt.price_delta_by_size || {}) };
                              if (v === '') delete next[sizeOpt.id];
                              else next[sizeOpt.id] = parseFloat(v) || 0;
                              updateOptionDraft(opt._key!, { price_delta_by_size: next });
                            }}
                            size="small"
                            sx={{ width: 150 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
              {optionDrafts.length === 0 && (
                <Typography variant="body2" sx={{ color: 'var(--admin-text-secondary)', textAlign: 'center', py: 2 }}>
                  No options yet. Click "Add Option".
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid #2a2d3e', px: 3, py: 2, bgcolor: '#0A2540' }}>
            <Button onClick={closeEditor} startIcon={<Close />} sx={{ color: '#9ca3af' }}>Cancel</Button>
            <Button variant="contained" onClick={saveGroup} startIcon={<Save />} sx={{ bgcolor: '#00C896', color: '#000', '&:hover': { bgcolor: '#7bc211' } }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default ModifiersPage;
